// Operating currency — the organisation's default currency drives all money
// formatting. It is cached here (set once the active org is known) so the pure
// `money()` formatter stays synchronous and never needs an async lookup.
//
// The authoritative value lives in `organizations.currency` (persisted via the
// Organization Settings page); this module is just a synchronous mirror for the
// many pure render helpers that format amounts.

interface CurrencyConfig {
  code: string;
  symbol: string;
}

const CURRENCIES: Record<string, CurrencyConfig> = {
  USD: { code: "USD", symbol: "$" },
  EUR: { code: "EUR", symbol: "€" },
  GBP: { code: "GBP", symbol: "£" },
  AUD: { code: "AUD", symbol: "A$" },
  CAD: { code: "CAD", symbol: "C$" },
};

let activeCurrency = "USD";

/** Set the active organisation's operating currency (call when the org loads/switches). */
export function setActiveCurrency(code: string | null | undefined): void {
  if (code && CURRENCIES[code.toUpperCase()]) activeCurrency = code.toUpperCase();
}

export function getActiveCurrency(): CurrencyConfig {
  return CURRENCIES[activeCurrency] ?? CURRENCIES.USD;
}

/** The active organisation's ISO currency code (e.g. "USD"), used when persisting amounts. */
export function getActiveCurrencyCode(): string {
  return getActiveCurrency().code;
}

/** Symbol for a specific currency code, falling back to the active org's symbol. */
export function currencySymbol(code?: string | null): string {
  if (code && CURRENCIES[code.toUpperCase()]) return CURRENCIES[code.toUpperCase()].symbol;
  return getActiveCurrency().symbol;
}